import { useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	PageIncludeError,
	ParsedPageIncludeTarget,
	ResolvedTarget,
} from '../pageIncludeMacroTypes.ts';
import { getPageIncludeQueryTarget } from '../util/pageIncludeMacroParams.ts';

import type {
	usePageIncludeMacroQueryByIdQuery$data,
	usePageIncludeMacroQueryByIdQuery$variables,
} from './__generated__/usePageIncludeMacroQueryByIdQuery.graphql.ts';
import type {
	usePageIncludeMacroQueryByTitleQuery$data,
	usePageIncludeMacroQueryByTitleQuery$variables,
} from './__generated__/usePageIncludeMacroQueryByTitleQuery.graphql.ts';

export const PageIncludeByIdQuery: GraphQLTaggedNode = graphql`
	query usePageIncludeMacroQueryByIdQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			id
			title
			type
			space {
				key
				id
			}
			body {
				atlas_doc_format {
					value
					mediaToken {
						token
					}
				}
			}
		}
	}
`;

export const PageIncludeByTitleQuery: GraphQLTaggedNode = graphql`
	query usePageIncludeMacroQueryByTitleQuery(
		$rawSpaceKey: String!
		$rawTitle: String!
		$includeRaw: Boolean!
		$splitSpaceKey: String!
		$splitTitle: String!
		$includeSplit: Boolean!
		$blogSpaceKey: String!
		$blogTitle: String!
		$postingDay: String!
		$includeBlog: Boolean!
	) {
		rawContent: content(spaceKey: $rawSpaceKey, title: $rawTitle, type: "page", first: 1)
			@include(if: $includeRaw) {
			nodes {
				id
				title
				type
				space {
					key
					id
				}
				body {
					atlas_doc_format {
						value
						mediaToken {
							token
						}
					}
				}
			}
		}
		splitContent: content(spaceKey: $splitSpaceKey, title: $splitTitle, type: "page", first: 1)
			@include(if: $includeSplit) {
			nodes {
				id
				title
				type
				space {
					key
					id
				}
				body {
					atlas_doc_format {
						value
						mediaToken {
							token
						}
					}
				}
			}
		}
		blogContent: content(
			spaceKey: $blogSpaceKey
			title: $blogTitle
			type: "blogpost"
			postingDay: $postingDay
			first: 1
		) @include(if: $includeBlog) {
			nodes {
				id
				title
				type
				space {
					key
					id
				}
				body {
					atlas_doc_format {
						value
						mediaToken {
							token
						}
					}
				}
			}
		}
	}
`;

export interface UsePageIncludeMacroQueryResult {
	loading: boolean;
	error: PageIncludeError | null;
	resolvedTarget: ResolvedTarget | null;
	adfString: string | null;
	mediaToken: string | undefined;
}

type PageIncludeContentById = NonNullable<usePageIncludeMacroQueryByIdQuery$data['singleContent']>;
type PageIncludeContentByTitleConnection = NonNullable<
	usePageIncludeMacroQueryByTitleQuery$data['rawContent']
>;
type PageIncludeContentByTitle = NonNullable<
	NonNullable<PageIncludeContentByTitleConnection['nodes']>[number]
>;
type PageIncludeQueryContent = PageIncludeContentById | PageIncludeContentByTitle;

const createEmptyResult = ({
	loading,
	error,
}: {
	loading: boolean;
	error: PageIncludeError | null;
}): UsePageIncludeMacroQueryResult => ({
	loading,
	error,
	resolvedTarget: null,
	adfString: null,
	mediaToken: undefined,
});

const createErrorResult = (error: PageIncludeError): UsePageIncludeMacroQueryResult =>
	createEmptyResult({ loading: false, error });

const getSupportedContentType = (
	content: PageIncludeQueryContent,
): ResolvedTarget['contentType'] | null => {
	if (content.type === 'page' || content.type === 'blogpost') {
		return content.type;
	}

	return null;
};

const createResolvedContentResult = (
	content: PageIncludeQueryContent,
): UsePageIncludeMacroQueryResult => {
	const contentType = getSupportedContentType(content);
	if (!contentType) {
		return createErrorResult('invalid-content-type');
	}

	const atlasDocFormat = content.body?.atlas_doc_format;

	return {
		loading: false,
		error: null,
		resolvedTarget: {
			contentId: content.id ?? '',
			title: content.title ?? '',
			spaceKey: content.space?.key ?? '',
			spaceId: content.space?.id ?? undefined,
			contentType,
		},
		adfString: atlasDocFormat?.value ?? null,
		mediaToken: atlasDocFormat?.mediaToken?.token ?? undefined,
	};
};

const loadingResult = createEmptyResult({ loading: true, error: null });
const missingTargetResult = createErrorResult('missing-target');
const networkFailureResult = createErrorResult('network-failure');
const notFoundResult = createErrorResult('not-found');

const emptyTitleQueryVariables: usePageIncludeMacroQueryByTitleQuery$variables = {
	rawSpaceKey: '',
	rawTitle: '',
	includeRaw: false,
	splitSpaceKey: '',
	splitTitle: '',
	includeSplit: false,
	blogSpaceKey: '',
	blogTitle: '',
	postingDay: '',
	includeBlog: false,
};

const getTitleQueryContent = (
	titleData: usePageIncludeMacroQueryByTitleQuery$data | undefined,
): PageIncludeContentByTitle | null => {
	const contentByBlog = titleData?.blogContent?.nodes?.[0];
	if (contentByBlog) {
		return contentByBlog;
	}

	const contentBySplit = titleData?.splitContent?.nodes?.[0];
	if (contentBySplit) {
		return contentBySplit;
	}

	return titleData?.rawContent?.nodes?.[0] ?? null;
};

/**
 * 1. If parsedTarget has a typed confluence-content:// URL, extract the ID
 *    and use singleContent(id).
 * 2. Otherwise query the possible legacy targets in one shot:
 *    - same-space raw title
 *    - valid "SPACEKEY:Title" split candidate
 *    - blog post date-path candidate
 *
 * The host-page spaceKey (from macroDefaultProps) is used as a fallback when
 * the legacy location has no explicit space prefix.
 */
export const usePageIncludeMacroQuery = (
	parsedTarget: ParsedPageIncludeTarget,
	hostSpaceKey: string | undefined,
): UsePageIncludeMacroQueryResult => {
	const queryTarget = getPageIncludeQueryTarget(parsedTarget, hostSpaceKey);
	const shouldUseIdQuery = queryTarget.kind === 'by-id';

	const idQueryResult = useQueryInterop<
		usePageIncludeMacroQueryByIdQuery$data,
		usePageIncludeMacroQueryByIdQuery$variables
	>(PageIncludeByIdQuery, {
		variables: shouldUseIdQuery ? queryTarget.variables : { contentId: '' },
		skip: !shouldUseIdQuery,
	});

	const titleQueryResult = useQueryInterop<
		usePageIncludeMacroQueryByTitleQuery$data,
		usePageIncludeMacroQueryByTitleQuery$variables
	>(PageIncludeByTitleQuery, {
		variables: queryTarget.kind === 'by-title' ? queryTarget.variables : emptyTitleQueryVariables,
		skip: queryTarget.kind !== 'by-title',
		fetchPolicy: 'cache-first',
	});

	return useMemo((): UsePageIncludeMacroQueryResult => {
		// If no valid target, we should not have reached here (caller handles missing-target)
		if (queryTarget.kind === 'missing-target') {
			return missingTargetResult;
		}

		if (shouldUseIdQuery) {
			const { data: idData, loading: idLoading, error: idError } = idQueryResult;
			if (idLoading) return loadingResult;
			if (idError) return networkFailureResult;

			const contentById = idData?.singleContent;
			return contentById ? createResolvedContentResult(contentById) : notFoundResult;
		}

		const { data: titleData, loading: titleLoading, error: titleError } = titleQueryResult;
		if (titleLoading) return loadingResult;
		if (titleError) return networkFailureResult;

		const contentByTitle = getTitleQueryContent(titleData);
		return contentByTitle ? createResolvedContentResult(contentByTitle) : notFoundResult;
	}, [queryTarget.kind, shouldUseIdQuery, idQueryResult, titleQueryResult]);
};
